import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import {
  Activity,
  BadgeCheck,
  Database,
  Plus,
  Search,
  Shield,
  ShieldAlert,
  Trash2
} from 'lucide-react';
import {
  DELETE_POLICY_ALIAS,
  GET_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG,
  LIST_POLICY_ALIASES,
  SAVE_POLICY_ALIAS,
  SAVE_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG
} from './userAccounts/queries';
import {
  DEFAULT_USER_MANAGER_PERMISSION_TEMPLATES,
  normalizeUserManagerPermissionTemplateConfig,
  USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS,
  UserManagerPermissionTemplate
} from './userAccounts/userManagerPermissionTemplates';
import {
  formatOrganizationalAliasLabel,
  mergeOrganizationalAliases
} from './userAccounts/organizationalAliases';
const ResolverPolicyReportView = require('./securityPolicyReport/ResolverPolicyReportPage').ResolverPolicyReportView as React.ComponentType<{
  embedded?: boolean;
  title?: string;
  description?: string;
}>;

const GET_GQL_SCHEMA = gql`
  query GetGqlSchema {
    getGqlSchema
  }
`;

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query SecurityManager2RequestOptions {
    roleOptions: userAccountRequestOptions(optionType: "role") {
      id
      optionType
      label
      sortOrder
      isActive
    }
    organizationOptions: userAccountRequestOptions(optionType: "organization") {
      id
      optionType
      label
      sortOrder
      isActive
    }
  }
`;

type SchemaKind = 'query' | 'mutation' | 'subscription';

type Endpoint = {
  name: string;
  kind: SchemaKind;
  returnType?: string;
  description?: string;
  deprecated?: boolean;
  path?: string;
  folderName?: string;
  resourcePath?: string | null;
  verb?: string | null;
  permissions?: string[] | null;
  permissionsSource?: string;
  permissionsDbState?: string;
  authentication?: any;
};

type SchemaPayload = {
  endpoints?: Endpoint[];
  types?: Record<string, any>;
};

type AliasRecord = {
  id: number | null;
  alias: string;
  type?: string | null;
  active?: boolean | null;
  description?: string | null;
  policies?: string[] | null;
};

type UserAccountRequestOption = {
  id: number;
  optionType: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
};

type UserAccountRequestOptionsResponse = {
  roleOptions: UserAccountRequestOption[];
  organizationOptions: UserAccountRequestOption[];
};

type UserManagerPermissionTemplateConfigResponse = {
  getUserManagerPermissionTemplateConfig?: {
    templates?: UserManagerPermissionTemplate[];
  };
};

type AliasType = 'group' | 'policy' | 'legacy' | 'hidden' | 'organizational';
type AliasTypeFilter = 'all' | AliasType;

type ParsedFeature = {
  namespace: string;
  resource: string;
  subresource: string;
  field: string;
  action: string;
};

const normalizeString = (value: unknown) => `${value || ''}`.trim();
const toTemplateDomId = (value: unknown) => normalizeString(value).replace(/[^a-z0-9]+/gi, '-');

const isOrganizationalAliasName = (value: unknown) => {
  const parts = `${value || ''}`
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length === 2;
};

const normalizeAliasType = (value: unknown): AliasType | '' => {
  const normalized = normalizeString(value).toLowerCase();
  if (
    normalized === 'group' ||
    normalized === 'policy' ||
    normalized === 'legacy' ||
    normalized === 'hidden' ||
    normalized === 'organizational'
  ) {
    return normalized;
  }
  return '';
};

const resolveAliasType = (alias: AliasRecord): AliasType => {
  const explicitType = normalizeAliasType(alias?.type);
  if (explicitType) {
    return explicitType;
  }
  const normalizedAlias = normalizeString(alias?.alias).toLowerCase();
  if (normalizedAlias.startsWith('legacy:')) {
    return 'legacy';
  }
  if (normalizedAlias.startsWith('hidden:')) {
    return 'hidden';
  }
  if (isOrganizationalAliasName(alias?.alias)) {
    return 'organizational';
  }
  return 'group';
};

const aliasTypeLabel: Record<AliasType, string> = {
  group: 'Group',
  policy: 'Feature',
  legacy: 'Legacy',
  hidden: 'Hidden',
  organizational: 'Organizational'
};

const aliasTypeFilterLabel: Record<AliasTypeFilter, string> = {
  all: 'All',
  group: 'Group',
  policy: 'Feature',
  legacy: 'Legacy',
  hidden: 'Hidden',
  organizational: 'Organizational'
};

const aliasTypeBadgeClass: Record<AliasType, string> = {
  group: 'border border-blue-200 bg-blue-100 text-blue-800',
  policy: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  legacy: 'border border-amber-200 bg-amber-100 text-amber-800',
  hidden: 'border border-slate-200 bg-slate-100 text-slate-700',
  organizational: 'border border-violet-200 bg-violet-100 text-violet-800'
};

const normalizeFeature = (value: string) => {
  const raw = normalizeString(value);
  if (!raw) return '';
  const [left, action] = raw.split('~');
  if (!left || !action) return '';
  const parts = left.split(':');
  if (parts.some((p) => !p)) return '';
  return `${parts.join(':')}~${action}`;
};

const normalizeAliasMember = (value: string) => {
  const raw = normalizeString(value);
  if (!raw) {
    return '';
  }
  const normalizedFeature = normalizeFeature(raw);
  if (normalizedFeature) {
    return normalizedFeature;
  }
  return raw;
};

const normalizeAliasMembers = (list?: string[] | null) =>
  Array.isArray(list) ? Array.from(new Set(list.map((p) => normalizeAliasMember(p)).filter(Boolean))) : [];

export const buildFeatureString = (
  root: string,
  resource: string,
  subresource: string,
  field: string,
  action: string
) => {
  if (!root || !resource || !action) return '';
  if (root === 'type') {
    const f = field || '*';
    return normalizeFeature(`type:${resource}:${f}~${action}`);
  }
  if (!subresource || subresource === '*') {
    return normalizeFeature(`${root}:${resource}~${action}`);
  }
  return normalizeFeature(`${root}:${resource}:${subresource}~${action}`);
};

export const parseFeatureString = (value: string): ParsedFeature | null => {
  const normalized = normalizeFeature(value);
  if (!normalized) {
    return null;
  }
  const [left, action] = normalized.split('~');
  if (!left || !action) {
    return null;
  }
  const parts = left.split(':').filter(Boolean);
  if (parts.length < 2) {
    return null;
  }
  if (parts[0] === 'type') {
    return {
      namespace: 'type',
      resource: parts[1] || '*',
      subresource: '*',
      field: parts.slice(2).join(':') || '*',
      action
    };
  }
  return {
    namespace: parts[0] || '*',
    resource: parts[1] || '*',
    subresource: parts.slice(2).join(':') || '*',
    field: '*',
    action
  };
};

type FeatureCatalog = {
  namespaces: string[];
  resourcesByNamespace: Map<string, Set<string>>;
  subresourcesByPath: Map<string, Set<string>>;
  fieldsByType: Map<string, Set<string>>;
  actionsByPath: Map<string, Set<string>>;
  allActions: string[];
};

const createPathKey = (namespace: string, resource: string, detail: string) => {
  return [namespace || '*', resource || '*', detail || '*'].join('|');
};

const addMappedValue = (map: Map<string, Set<string>>, key: string, value: string) => {
  const next = map.get(key) || new Set<string>(['*']);
  next.add(value || '*');
  map.set(key, next);
};

export const buildMountedFeatureCatalog = (features: string[]): FeatureCatalog => {
  const namespaces = new Set<string>(['*']);
  const resourcesByNamespace = new Map<string, Set<string>>();
  const subresourcesByPath = new Map<string, Set<string>>();
  const fieldsByType = new Map<string, Set<string>>();
  const actionsByPath = new Map<string, Set<string>>();
  const allActions = new Set<string>();

  features.forEach((feature) => {
    const parsed = parseFeatureString(feature);
    if (!parsed) {
      return;
    }

    namespaces.add(parsed.namespace);
    addMappedValue(resourcesByNamespace, parsed.namespace, parsed.resource || '*');

    if (parsed.namespace === 'type') {
      addMappedValue(fieldsByType, parsed.resource || '*', parsed.field || '*');
      addMappedValue(actionsByPath, createPathKey(parsed.namespace, parsed.resource, parsed.field || '*'), parsed.action);
      allActions.add(parsed.action);
      return;
    }

    addMappedValue(subresourcesByPath, createPathKey(parsed.namespace, parsed.resource, '*'), parsed.subresource || '*');
    addMappedValue(actionsByPath, createPathKey(parsed.namespace, parsed.resource, parsed.subresource || '*'), parsed.action);
    allActions.add(parsed.action);
  });

  return {
    namespaces: Array.from(namespaces).sort((left, right) => left.localeCompare(right)),
    resourcesByNamespace,
    subresourcesByPath,
    fieldsByType,
    actionsByPath,
    allActions: Array.from(allActions).sort((left, right) => left.localeCompare(right))
  };
};

const getDraftAliasType = (aliasName: string, aliasType: unknown): AliasType => {
  if (isOrganizationalAliasName(aliasName)) {
    return 'organizational';
  }
  return normalizeAliasType(aliasType) || 'group';
};

const getAliasDisplayLabel = (alias: AliasRecord) => {
  if (resolveAliasType(alias) !== 'organizational') {
    return alias.alias;
  }
  return formatOrganizationalAliasLabel(alias.alias);
};

const parseSchemaPayload = (raw?: string | null): SchemaPayload => {
  if (!raw) return { endpoints: [], types: {} };
  try {
    const parsed = JSON.parse(raw);
    return {
      endpoints: Array.isArray(parsed?.endpoints) ? parsed.endpoints : [],
      types: parsed?.types && typeof parsed.types === 'object' ? parsed.types : {}
    };
  } catch (error) {
    return { endpoints: [], types: {} };
  }
};

const statusOf = (entry: Endpoint) => {
  if (entry.authentication?.unauthenticated) return 'unauth';
  if (`${entry.resourcePath || entry.path || ''}`.startsWith('messages')) return 'mail';
  if (entry.permissionsDbState === 'missing' || (entry.permissions || []).length === 0) return 'missing';
  return 'ok';
};

export const SecurityManager2 = () => {
  const { data: schemaData, loading: schemaLoading, error: schemaError } =
    useQuery<{ getGqlSchema: string }>(GET_GQL_SCHEMA);
  const {
    data: aliasData,
    loading: aliasLoading,
    refetch: refetchAliases
  } = useQuery<{ listPolicyAliases: AliasRecord[] }>(LIST_POLICY_ALIASES);
  const { data: optionData } = useQuery<UserAccountRequestOptionsResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS);
  const {
    data: templateConfigData,
    refetch: refetchTemplateConfig
  } = useQuery<UserManagerPermissionTemplateConfigResponse>(GET_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG);
  const [saveAlias, { loading: savingAlias }] = useMutation(SAVE_POLICY_ALIAS);
  const [deleteAlias, { loading: deletingAlias }] = useMutation(DELETE_POLICY_ALIAS);
  const [saveTemplateConfig, { loading: savingTemplateConfig }] = useMutation(SAVE_USER_MANAGER_PERMISSION_TEMPLATE_CONFIG);

  const [tab, setTab] = useState<'summary' | 'functions' | 'groups' | 'user-manager'>('functions');

  const [namespace, setNamespace] = useState('*');
  const [resource, setResource] = useState('*');
  const [subresource, setSubresource] = useState('*');
  const [action, setAction] = useState('read');
  const [field, setField] = useState('*');

  const [groupSearch, setGroupSearch] = useState('');
  const [groupTypeFilter, setGroupTypeFilter] = useState<AliasTypeFilter>('all');
  const [aliasReferenceToAdd, setAliasReferenceToAdd] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isNewGroup, setIsNewGroup] = useState(false);
  const [groupDraft, setGroupDraft] = useState<AliasRecord>({
    id: 0,
    alias: '',
    type: 'group',
    description: '',
    policies: []
  });
  const [directPolicyString, setDirectPolicyString] = useState('');
  const [templateDrafts, setTemplateDrafts] = useState<UserManagerPermissionTemplate[]>(
    normalizeUserManagerPermissionTemplateConfig({ templates: DEFAULT_USER_MANAGER_PERMISSION_TEMPLATES }).templates
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const schemaPayload = useMemo(
    () => parseSchemaPayload(schemaData?.getGqlSchema),
    [schemaData?.getGqlSchema]
  );

  const endpoints: Endpoint[] = useMemo(() => schemaPayload.endpoints || [], [schemaPayload.endpoints]);

  const kindCounts = useMemo(() => {
    const counts: Record<SchemaKind, number> = { query: 0, mutation: 0, subscription: 0 };
    endpoints.forEach((i) => (counts[i.kind] += 1));
    return counts;
  }, [endpoints]);

  const unauthList = useMemo(() => endpoints.filter((i) => statusOf(i) === 'unauth'), [endpoints]);
  const mailList = useMemo(() => endpoints.filter((i) => statusOf(i) === 'mail'), [endpoints]);
  const missingList = useMemo(() => endpoints.filter((i) => statusOf(i) === 'missing'), [endpoints]);

  const organizationOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.organizationOptions) ? optionData?.organizationOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && normalizeString(row?.label).length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return normalizeString(left?.label).localeCompare(normalizeString(right?.label));
      })
      .map((row) => normalizeString(row?.label));
  }, [optionData?.organizationOptions]);

  const roleOptions = useMemo(() => {
    const rows = Array.isArray(optionData?.roleOptions) ? optionData?.roleOptions || [] : [];
    return rows
      .filter((row) => row?.isActive !== false && normalizeString(row?.label).length > 0)
      .sort((left, right) => {
        const sortDelta = Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0);
        if (sortDelta !== 0) {
          return sortDelta;
        }
        return normalizeString(left?.label).localeCompare(normalizeString(right?.label));
      })
      .map((row) => normalizeString(row?.label));
  }, [optionData?.roleOptions]);

  const mergedAliases = useMemo(() => {
    return mergeOrganizationalAliases(aliasData?.listPolicyAliases || [], organizationOptions, roleOptions).map((alias) => ({
      ...alias,
      id: alias.id ?? null
    })) as AliasRecord[];
  }, [aliasData?.listPolicyAliases, organizationOptions, roleOptions]);

  const schemaPolicies = useMemo(() => {
    const policies = new Set<string>();
    endpoints.forEach((entry) => {
      normalizeAliasMembers(entry?.permissions).forEach((permission) => {
        const normalized = normalizeFeature(permission);
        if (!normalized) {
          return;
        }
        policies.add(normalized);
      });
    });
    return Array.from(policies);
  }, [endpoints]);

  const featureCatalog = useMemo(() => buildMountedFeatureCatalog(schemaPolicies), [schemaPolicies]);

  const namespaceOptions = useMemo(() => {
    const options = new Set(featureCatalog.namespaces);
    if (namespace) {
      options.add(namespace);
    }
    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [featureCatalog.namespaces, namespace]);

  const resourceOptions = useMemo(() => {
    if (namespace === '*') {
      return ['*'];
    }
    const options = new Set(featureCatalog.resourcesByNamespace.get(namespace) || ['*']);
    options.add('*');
    if (resource) {
      options.add(resource);
    }
    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [featureCatalog.resourcesByNamespace, namespace, resource]);

  const subresourceOptions = useMemo(() => {
    if (namespace === '*' || namespace === 'type') {
      return ['*'];
    }
    const options = new Set(
      featureCatalog.subresourcesByPath.get(createPathKey(namespace, resource, '*')) || ['*']
    );
    options.add('*');
    if (subresource) {
      options.add(subresource);
    }
    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [featureCatalog.subresourcesByPath, namespace, resource, subresource]);

  const fieldOptions = useMemo(() => {
    if (namespace !== 'type') {
      return ['*'];
    }
    const options = new Set(featureCatalog.fieldsByType.get(resource) || ['*']);
    options.add('*');
    if (field) {
      options.add(field);
    }
    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [featureCatalog.fieldsByType, field, namespace, resource]);

  const actionOptions = useMemo(() => {
    const options = new Set<string>();

    if (namespace === 'type') {
      [createPathKey(namespace, resource, field), createPathKey(namespace, resource, '*')].forEach((key) => {
        (featureCatalog.actionsByPath.get(key) || []).forEach((item) => options.add(item));
      });
    } else if (namespace !== '*') {
      [createPathKey(namespace, resource, subresource), createPathKey(namespace, resource, '*')].forEach((key) => {
        (featureCatalog.actionsByPath.get(key) || []).forEach((item) => options.add(item));
      });
    }

    if (options.size === 0) {
      featureCatalog.allActions.forEach((item) => options.add(item));
    }
    if (action) {
      options.add(action);
    }

    return Array.from(options).sort((left, right) => left.localeCompare(right));
  }, [action, featureCatalog.actionsByPath, featureCatalog.allActions, field, namespace, resource, subresource]);

  const groupList = useMemo(() => {
    const term = groupSearch.trim().toLowerCase();
    const list = mergedAliases;

    const filtered = list.filter((g) => {
      const aliasType = resolveAliasType(g);
      if (term && !g.alias.toLowerCase().includes(term) && !getAliasDisplayLabel(g).toLowerCase().includes(term)) return false;
      if (groupTypeFilter !== 'all' && aliasType !== groupTypeFilter) return false;
      return true;
    });

    return filtered
      .map((group) => ({ ...group, type: resolveAliasType(group) }))
      .sort((a, b) => a.alias.localeCompare(b.alias));
  }, [mergedAliases, groupSearch, groupTypeFilter]);

  const groupTypeCounts = useMemo(() => {
    const counts: Record<AliasTypeFilter, number> = {
      all: 0,
      group: 0,
      policy: 0,
      legacy: 0,
      hidden: 0,
      organizational: 0
    };
    mergedAliases.forEach((alias) => {
      const aliasType = resolveAliasType(alias);
      counts.all += 1;
      counts[aliasType] += 1;
    });
    return counts;
  }, [mergedAliases]);

  useEffect(() => {
    if (isNewGroup) return;
    const hasSelection =
      selectedGroup && groupList.some((g) => g.alias === selectedGroup);
    if (hasSelection) return;
    if (groupList.length === 0) {
      setSelectedGroup(null);
      return;
    }
    if (!selectedGroup && groupList.length > 0) {
      setSelectedGroup(groupList[0].alias);
      return;
    }
    setSelectedGroup(groupList[0].alias);
  }, [groupList, selectedGroup, isNewGroup]);

  useEffect(() => {
    if (!selectedGroup) return;
    const match = groupList.find((g) => g.alias === selectedGroup);
    if (!match) return;
    setIsNewGroup(false);
    setGroupDraft({
      id: match.id,
      alias: match.alias,
      type: resolveAliasType(match),
      description: match.description || '',
      policies: normalizeAliasMembers(match.policies)
    });
  }, [selectedGroup, groupList]);

  useEffect(() => {
    if (namespace === '*') {
      if (resource !== '*') {
        setResource('*');
      }
      if (subresource !== '*') {
        setSubresource('*');
      }
      return;
    }
    if (resourceOptions.includes(resource)) {
      return;
    }
    setResource(resourceOptions[0] || '*');
  }, [namespace, resource, resourceOptions, subresource]);

  useEffect(() => {
    if (namespace === '*' || namespace === 'type') {
      if (subresource !== '*') {
        setSubresource('*');
      }
      return;
    }
    if (subresourceOptions.includes(subresource)) {
      return;
    }
    setSubresource(subresourceOptions[0] || '*');
  }, [namespace, subresource, subresourceOptions]);

  useEffect(() => {
    if (namespace !== 'type') {
      if (field !== '*') {
        setField('*');
      }
      return;
    }
    if (fieldOptions.includes(field)) {
      return;
    }
    setField(fieldOptions[0] || '*');
  }, [field, fieldOptions, namespace]);

  const aliasTypeByName = useMemo(() => {
    const map = new Map<string, AliasType>();
    mergedAliases.forEach((alias) => {
      const aliasName = normalizeString(alias?.alias);
      if (!aliasName) {
        return;
      }
      map.set(aliasName, resolveAliasType(alias));
    });
    return map;
  }, [mergedAliases]);

  const aliasReferenceOptions = useMemo(() => {
    const currentAliasKey = normalizeString(groupDraft.alias).toLowerCase();
    const assignedKeys = new Set(normalizeAliasMembers(groupDraft.policies).map((policy) => normalizeString(policy).toLowerCase()));
    return mergedAliases
      .filter((alias) => alias?.active !== false)
      .filter((alias) => resolveAliasType(alias) !== 'organizational')
      .map((alias) => normalizeString(alias.alias))
      .filter((aliasName) => aliasName.length > 0)
      .filter((aliasName) => aliasName.toLowerCase() !== currentAliasKey)
      .filter((aliasName) => !assignedKeys.has(aliasName.toLowerCase()))
      .sort((left, right) => left.localeCompare(right));
  }, [mergedAliases, groupDraft.alias, groupDraft.policies]);

  useEffect(() => {
    const normalized = normalizeUserManagerPermissionTemplateConfig(
      templateConfigData?.getUserManagerPermissionTemplateConfig
    );
    setTemplateDrafts(normalized.templates);
    setSelectedTemplateId((previous) => {
      if (previous && normalized.templates.some((template) => template.id === previous)) {
        return previous;
      }
      return normalized.templates[0]?.id || '';
    });
  }, [templateConfigData]);

  const selectedTemplateDraft = useMemo(() => {
    return templateDrafts.find((template) => template.id === selectedTemplateId) || templateDrafts[0] || null;
  }, [selectedTemplateId, templateDrafts]);

  const templateOptionsByCategory = useMemo(() => {
    const groups = new Map<string, typeof USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS>();
    USER_MANAGER_PERMISSION_TEMPLATE_OPTIONS.forEach((option) => {
      if (!groups.has(option.category)) {
        groups.set(option.category, []);
      }
      groups.get(option.category)?.push(option);
    });
    return Array.from(groups.entries());
  }, []);

  const updateTemplateDraft = (templateId: string, updates: Partial<UserManagerPermissionTemplate>) => {
    setTemplateDrafts((previous) =>
      previous.map((template) =>
        template.id === templateId
          ? normalizeUserManagerPermissionTemplateConfig({
              templates: [{ ...template, ...updates }]
            }).templates[0] || template
          : template
      )
    );
  };

  const toggleTemplatePermission = (templateId: string, permission: string, enabled: boolean) => {
    setTemplateDrafts((previous) =>
      previous.map((template) => {
        if (template.id !== templateId) {
          return template;
        }
        const existing = new Set(template.permissions);
        if (enabled) {
          existing.add(permission);
        } else {
          existing.delete(permission);
        }
        return normalizeUserManagerPermissionTemplateConfig({
          templates: [{ ...template, permissions: Array.from(existing) }]
        }).templates[0] || template;
      })
    );
  };

  const addTemplateDraft = () => {
    const nextNumber = templateDrafts.length + 1;
    const template = {
      id: `template-${nextNumber}`,
      label: `Template ${nextNumber}`,
      permissions: []
    };
    setTemplateDrafts((previous) => [...previous, template]);
    setSelectedTemplateId(template.id);
  };

  const deleteTemplateDraft = (templateId: string) => {
    setTemplateDrafts((previous) => {
      const next = previous.filter((template) => template.id !== templateId);
      setSelectedTemplateId(next[0]?.id || '');
      return next;
    });
  };

  const handleSaveTemplateConfig = async () => {
    const normalized = normalizeUserManagerPermissionTemplateConfig({ templates: templateDrafts });
    try {
      await saveTemplateConfig({
        variables: {
          config: normalized
        }
      });
      await refetchTemplateConfig();
      toast.success('User Manager templates saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save User Manager templates');
    }
  };

  const handleAddGroupFeature = async () => {
    const feature = buildFeatureString(namespace, resource, subresource, field, action);
    if (!feature) {
      toast.error('Feature is required');
      return;
    }
    const next = new Set(normalizeAliasMembers(groupDraft.policies));
    next.add(feature);
    const payload = {
      alias: groupDraft.alias.trim(),
      type: getDraftAliasType(groupDraft.alias, groupDraft.type),
      description: groupDraft.description || '',
      policies: Array.from(next)
    };
    if (!payload.alias) {
      toast.error('Group or feature name is required');
      return;
    }
    try {
      await saveAlias({ variables: { alias: payload } });
      await refetchAliases();
      toast.success('Group or feature saved');
    } catch (error) {
      toast.error('Failed to save group or feature');
    }
  };

  const handleAddAliasReference = async () => {
    const aliasName = normalizeString(aliasReferenceToAdd);
    if (!aliasName) {
      toast.error('Alias reference is required');
      return;
    }
    const next = new Set(normalizeAliasMembers(groupDraft.policies));
    next.add(aliasName);
    const payload = {
      alias: groupDraft.alias.trim(),
      type: getDraftAliasType(groupDraft.alias, groupDraft.type),
      description: groupDraft.description || '',
      policies: Array.from(next)
    };
    if (!payload.alias) {
      toast.error('Group or feature name is required');
      return;
    }
    try {
      await saveAlias({ variables: { alias: payload } });
      await refetchAliases();
      setAliasReferenceToAdd('');
      toast.success('Alias reference added');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to add alias reference');
    }
  };

  const handleRemoveGroupFeature = async (feature: string) => {
    const next = normalizeAliasMembers(groupDraft.policies).filter((p) => p !== feature);
    try {
      await saveAlias({
        variables: {
          alias: {
            alias: groupDraft.alias.trim(),
            type: getDraftAliasType(groupDraft.alias, groupDraft.type),
            description: groupDraft.description || '',
            policies: next
          }
        }
      });
      await refetchAliases();
    } catch (error) {
      toast.error('Failed to update group or feature');
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupDraft.id) return;
    try {
      await deleteAlias({ variables: { aliasId: groupDraft.id } });
      setSelectedGroup(null);
      await refetchAliases();
    } catch (error) {
      toast.error('Failed to delete group or feature');
    }
  };

  const handleAddDirectPolicyString = async () => {
    const normalized = normalizeAliasMember(directPolicyString);
    if (!normalized) {
      toast.error('Policy string is required');
      return;
    }
    const next = new Set(normalizeAliasMembers(groupDraft.policies));
    if (next.has(normalized)) {
      toast.error('Policy string already assigned');
      return;
    }
    next.add(normalized);
    const payload = {
      alias: groupDraft.alias.trim(),
      type: getDraftAliasType(groupDraft.alias, groupDraft.type),
      description: groupDraft.description || '',
      policies: Array.from(next)
    };
    if (!payload.alias) {
      toast.error('Group or feature name is required');
      return;
    }
    try {
      await saveAlias({ variables: { alias: payload } });
      await refetchAliases();
      setDirectPolicyString('');
      toast.success('Policy string added');
    } catch (error) {
      toast.error('Failed to add policy string');
    }
  };

  const selectedGroupType = getDraftAliasType(groupDraft.alias, groupDraft.type);
  const isLockedOrganizationalGroup = !isNewGroup && isOrganizationalAliasName(groupDraft.alias);
  const canDeleteGroup = typeof groupDraft.id === 'number' && groupDraft.id > 0;

  return (
    <div className="container mx-auto max-w-[112rem] space-y-3 p-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Security Manager v2</h1>
          <p className="text-xs text-gray-600">
            Read-only resolver audit plus group and feature management. Preview build.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="grid h-8 w-full max-w-[560px] grid-cols-4">
          <TabsTrigger className="text-xs" value="summary">Summary</TabsTrigger>
          <TabsTrigger className="text-xs" value="functions">Functions</TabsTrigger>
          <TabsTrigger className="text-xs" value="groups">Groups & Features</TabsTrigger>
          <TabsTrigger className="text-xs" value="user-manager">User Manager</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
            <SummaryTile label="Functions" value={endpoints.length} icon={<Database className="h-4 w-4" />} />
            <SummaryTile label="Queries" value={kindCounts.query} />
            <SummaryTile label="Mutations" value={kindCounts.mutation} />
            <SummaryTile label="Subscriptions" value={kindCounts.subscription} />
            <SummaryTile
              label="Missing entries"
              value={missingList.length}
              tone={missingList.length > 0 ? 'danger' : 'neutral'}
              icon={<ShieldAlert className="h-4 w-4" />}
            />
            <SummaryTile label="Unauthenticated" value={unauthList.length} tone="info" icon={<Activity className="h-4 w-4" />} />
            <SummaryTile label="Mail" value={mailList.length} tone="purple" icon={<BadgeCheck className="h-4 w-4" />} />
          </div>
        </TabsContent>

        <TabsContent value="functions" className="space-y-3">
          <ResolverPolicyReportView
            embedded
            title="Resolver Feature Audit"
            description="Read-only resolver list with inline features, issue status, and target feature guidance."
          />
        </TabsContent>

        <TabsContent value="user-manager" className="space-y-3" data-testid="user-manager-template-settings">
          <div className="grid gap-3 md:grid-cols-[280px_minmax(0,1fr)]">
            <div className="rounded-md border border-gray-200 bg-white">
              <div className="flex items-center justify-end border-b border-gray-200 p-3">
                <Button size="sm" variant="secondary" onClick={addTemplateDraft} data-testid="user-manager-template-add">
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
              <ScrollArea className="h-[640px]">
                <div className="divide-y divide-gray-100">
                  {templateDrafts.map((template) => (
                    <div
                      key={template.id}
                      data-testid={`user-manager-template-row-${toTemplateDomId(template.id)}`}
                      className={cn(
                        'group flex items-center gap-2 px-3 py-2',
                        selectedTemplateDraft?.id === template.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedTemplateId(template.id)}
                        data-testid={`user-manager-template-select-${toTemplateDomId(template.id)}`}
                        className="min-w-0 flex-1 text-left text-xs"
                      >
                        <div className="truncate font-medium text-gray-900">{template.label}</div>
                        <div className="text-[10px] text-gray-500">{template.permissions.length} selections</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTemplateDraft(template.id)}
                        className="shrink-0 rounded p-1 text-white opacity-0 hover:text-red-700 focus:opacity-100 focus:text-red-700 group-hover:opacity-100"
                        aria-label={`Delete ${template.label}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {templateDrafts.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-500">No templates configured.</div>
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="rounded-md border border-gray-200 bg-white p-3">
              {selectedTemplateDraft ? (
                <div className="space-y-4">
                  <div className="min-w-0">
                    <Input
                      aria-label="Template name"
                      value={selectedTemplateDraft.label}
                      onChange={(event) => updateTemplateDraft(selectedTemplateDraft.id, { label: event.target.value })}
                      data-testid="user-manager-template-label-input"
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="space-y-4">
                    {templateOptionsByCategory.map(([category, options]) => (
                      <section key={category} className="space-y-2">
                        <div className="text-xs font-semibold uppercase text-gray-500">{category}</div>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-3">
                          {options.map((option) => {
                            const inputId = `user-manager-template-${selectedTemplateDraft.id}-${toTemplateDomId(option.value)}`;
                            const checked = selectedTemplateDraft.permissions.includes(option.value);
                            return (
                              <label key={option.value} htmlFor={inputId} className="flex items-center gap-2 text-sm text-gray-900">
                                <input
                                  id={inputId}
                                  data-testid={`user-manager-template-option-${toTemplateDomId(option.value)}`}
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(event) =>
                                    toggleTemplatePermission(selectedTemplateDraft.id, option.value, event.target.checked)
                                  }
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>

                  <div className="flex justify-end border-t border-gray-100 pt-3">
                    <Button
                      size="sm"
                      onClick={handleSaveTemplateConfig}
                      disabled={savingTemplateConfig}
                      data-testid="user-manager-template-save"
                    >
                      {savingTemplateConfig ? 'Saving...' : 'Save templates'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No template selected.</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="groups" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-[300px_minmax(0,1fr)]">
            <div className="rounded-md border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Search className="h-4 w-4 text-gray-400" />
                  <Input
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="Search groups and features"
                    className="h-8 w-full min-w-0 text-xs"
                  />
                </div>
              </div>
              <div className="border-b border-gray-200 px-3 py-2">
                <Field label="Type">
                  <Select value={groupTypeFilter} onValueChange={(value) => setGroupTypeFilter(value as AliasTypeFilter)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(['all', 'group', 'organizational', 'policy', 'legacy', 'hidden'] as AliasTypeFilter[]).map((filter) => (
                        <SelectItem key={filter} value={filter}>
                          {`${aliasTypeFilterLabel[filter]} (${groupTypeCounts[filter]})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <ScrollArea className="h-[720px]">
                <div className="divide-y divide-gray-100">
                  {groupList.map((group) => {
                    return (
                      <button
                        key={group.alias}
                        onClick={() => {
                          setSelectedGroup(group.alias);
                          setIsNewGroup(false);
                        }}
                        className={cn(
                          'w-full px-2.5 py-2 text-left text-xs',
                          selectedGroup === group.alias ? 'bg-blue-50' : 'hover:bg-gray-50'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5 text-gray-500" />
                          <span className="font-medium text-gray-900">{getAliasDisplayLabel(group)}</span>
                          <Badge className={cn('text-[9px] font-semibold', aliasTypeBadgeClass[resolveAliasType(group)])}>
                            {aliasTypeLabel[resolveAliasType(group)]}
                          </Badge>
                        </div>
                        <div className="line-clamp-2 text-[10px] text-gray-500">
                          {group.description || 'No description'}
                        </div>
                      </button>
                    );
                  })}
                  {groupList.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-500">No groups or features found.</div>
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="space-y-3">
              <div className="rounded-md border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase text-gray-500">Group Or Feature</div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="text-sm font-semibold text-gray-900">
                        {isLockedOrganizationalGroup ? formatOrganizationalAliasLabel(groupDraft.alias) : groupDraft.alias || 'New group'}
                      </div>
                      <Badge className={cn('text-[9px] font-semibold', aliasTypeBadgeClass[selectedGroupType])}>
                        {aliasTypeLabel[selectedGroupType]}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setIsNewGroup(true);
                        setSelectedGroup(null);
                        setGroupDraft({ id: 0, alias: '', type: 'group', description: '', policies: [] });
                      }}
                    >
                      <Plus className="mr-1 h-4 w-4" /> New
                    </Button>
                    {canDeleteGroup && (
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={deletingAlias}
                        onClick={handleDeleteGroup}
                      >
                        <Trash2 className="mr-1 h-4 w-4" /> Delete
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid gap-2 md:grid-cols-3">
                  <Input
                    placeholder="Group or feature name"
                    value={isLockedOrganizationalGroup ? formatOrganizationalAliasLabel(groupDraft.alias) : groupDraft.alias}
                    onChange={(e) =>
                      setGroupDraft((prev) => ({ ...prev, alias: e.target.value }))
                    }
                    className="h-9 text-sm"
                    disabled={isLockedOrganizationalGroup}
                  />
                  <Input
                    placeholder="Short description"
                    value={groupDraft.description || ''}
                    onChange={(e) =>
                      setGroupDraft((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="h-9 text-sm"
                  />
                  <Select
                    value={selectedGroupType}
                    onValueChange={(value) =>
                      setGroupDraft((prev) => ({ ...prev, type: value }))
                    }
                    disabled={selectedGroupType === 'organizational'}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Alias type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Group</SelectItem>
                      {selectedGroupType === 'organizational' && (
                        <SelectItem value="organizational">Organizational</SelectItem>
                      )}
                      <SelectItem value="policy">Feature</SelectItem>
                      <SelectItem value="legacy">Legacy</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <Field label="Namespace">
                    <Select value={namespace} onValueChange={setNamespace}>
                      <SelectTrigger className="h-8 w-[140px] text-xs">
                        <SelectValue placeholder="Namespace" />
                      </SelectTrigger>
                      <SelectContent>
                        {namespaceOptions.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Resource">
                    <Select value={resource} onValueChange={setResource}>
                      <SelectTrigger className="h-8 w-[220px] text-xs">
                        <SelectValue placeholder="Resource" />
                      </SelectTrigger>
                      <SelectContent>
                        {resourceOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {namespace !== 'type' && (
                    <Field label="Subresource">
                      <Select value={subresource} onValueChange={setSubresource}>
                        <SelectTrigger className="h-8 w-[220px] text-xs">
                          <SelectValue placeholder="Subresource" />
                        </SelectTrigger>
                        <SelectContent>
                          {subresourceOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Field label="Action">
                    <Select value={action} onValueChange={setAction}>
                      <SelectTrigger className="h-8 w-[120px] text-xs">
                        <SelectValue placeholder="Action" />
                      </SelectTrigger>
                      <SelectContent>
                        {actionOptions.map((a) => (
                          <SelectItem key={a} value={a}>
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {namespace === 'type' && (
                    <Field label="Field">
                      <Select value={field} onValueChange={setField}>
                        <SelectTrigger className="h-8 w-[180px] text-xs">
                          <SelectValue placeholder="Field" />
                        </SelectTrigger>
                        <SelectContent>
                          {fieldOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Button size="sm" onClick={handleAddGroupFeature} disabled={savingAlias}>
                    <Plus className="mr-1 h-4 w-4" /> Add feature
                  </Button>
                </div>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <Field label="Alias Reference">
                    <Select value={aliasReferenceToAdd} onValueChange={setAliasReferenceToAdd}>
                      <SelectTrigger className="h-8 w-[260px] text-xs">
                        <SelectValue placeholder="Select alias to include" />
                      </SelectTrigger>
                      <SelectContent>
                        {aliasReferenceOptions.map((aliasName) => (
                          <SelectItem key={aliasName} value={aliasName}>
                            {formatOrganizationalAliasLabel(aliasName)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Button size="sm" variant="secondary" onClick={handleAddAliasReference} disabled={savingAlias || !aliasReferenceToAdd}>
                    <Plus className="mr-1 h-4 w-4" /> Add alias
                  </Button>
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase text-gray-500">Included Features</div>
                    <div className="text-[10px] text-gray-500">
                      {normalizeAliasMembers(groupDraft.policies).length} assigned
                    </div>
                  </div>
                  <ScrollArea className="h-[300px] pr-1">
                    <div className="space-y-1">
                      {normalizeAliasMembers(groupDraft.policies).map((feature) => (
                        <div
                          key={feature}
                          className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-2 py-1 text-xs"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="font-mono text-gray-800">{feature}</span>
                            {aliasTypeByName.has(feature) && (
                              <Badge className={cn('text-[9px] font-semibold', aliasTypeBadgeClass[aliasTypeByName.get(feature) || 'group'])}>
                                {aliasTypeLabel[aliasTypeByName.get(feature) || 'group']}
                              </Badge>
                            )}
                          </div>
                          <button
                            className="text-gray-500 hover:text-red-600"
                            onClick={() => handleRemoveGroupFeature(feature)}
                            aria-label={`Remove ${feature}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {normalizeAliasMembers(groupDraft.policies).length === 0 && (
                        <div className="text-xs text-gray-500">No features assigned.</div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input
                    value={directPolicyString}
                    onChange={(e) => setDirectPolicyString(e.target.value)}
                    placeholder="Add policy string directly to this group"
                    className="h-8 text-xs"
                  />
                  <Button size="sm" onClick={handleAddDirectPolicyString} disabled={savingAlias}>
                    Add string
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
      const payload = {
        alias: groupDraft.alias.trim(),
        type: getDraftAliasType(groupDraft.alias, groupDraft.type),
        description: groupDraft.description || '',
        policies: normalizeAliasMembers(groupDraft.policies)
      };
                      if (!payload.alias) {
                        toast.error('Group or feature name is required');
                        return;
                      }
                      try {
                        await saveAlias({ variables: { alias: payload } });
                        await refetchAliases();
                        toast.success('Group or feature saved');
                      } catch (error) {
                        toast.error('Failed to save group or feature');
                      }
                    }}
                    disabled={savingAlias}
                  >
                    Save group
                  </Button>
                </div>

              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {(schemaLoading || aliasLoading) && (
        <div className="text-xs text-gray-500">Loading data…</div>
      )}
      {schemaError && <div className="text-xs text-red-600">{schemaError.message}</div>}
    </div>
  );
};

const SummaryTile = ({
  label,
  value,
  tone = 'neutral',
  icon
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'danger' | 'info' | 'purple';
  icon?: React.ReactNode;
}) => {
  const toneClass =
    tone === 'danger'
      ? 'text-red-700'
      : tone === 'info'
      ? 'text-blue-700'
      : tone === 'purple'
      ? 'text-purple-700'
      : 'text-gray-900';
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white p-2.5 shadow-sm">
      <div className="rounded bg-gray-100 p-1.5 text-gray-600">{icon}</div>
      <div>
        <div className="text-[10px] font-semibold uppercase text-gray-500">{label}</div>
        <div className={cn('text-lg font-semibold', toneClass)}>{value}</div>
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-600">
      <span>{label}</span>
      {children}
    </label>
  );
};
